import http from "http";
import open from "open";

import { OidcConfig } from "../config/schemas.js";
import { logger } from "../logger.js";

import {
  authorizationCodeGrant,
  discovery,
  buildAuthorizationUrl,
  TokenEndpointResponse,
  TokenEndpointResponseHelpers,
  randomState,
  calculatePKCECodeChallenge,
  randomPKCECodeVerifier,
} from "openid-client";

/**
 * Convenience type for the response from the token endpoint.
 *
 * This is a union of the TokenEndpointResponse and TokenEndpointResponseHelpers
 * from the openid-client library.
 */
export type TokensResponse = TokenEndpointResponse &
  TokenEndpointResponseHelpers;

// Define interface for JWT claims based on standard OpenID Connect claims
// that match our requested scopes: 'openid profile email'
/**
 * Interface for JWT claims that aligns with the OpenID Connect (OIDC) specification.
 *
 * These fields are defined in the OIDC Core specification:
 * https://openid.net/specs/openid-connect-core-1_0.html#StandardClaims
 *
 * We request the scopes 'openid profile email' which provides access to these claims.
 * The actual claims returned will depend on the identity provider and user permissions.
 */
export interface UserJwtClaims {
  // Required by OpenID Connect
  sub: string;

  // Email scope
  email?: string;
  email_verified?: boolean;

  // Profile scope
  name?: string;
  given_name?: string;
  family_name?: string;
  middle_name?: string;
  nickname?: string;
  preferred_username?: string;
  profile?: string;
  picture?: string;
  website?: string;
  gender?: string;
  birthdate?: string;
  zoneinfo?: string;
  locale?: string;
  updated_at?: number;

  // JWT standard fields
  iat?: number;
  exp?: number;
  iss?: string;
}

class OAuthCallbackServer {
  private port: number;
  private server: http.Server;

  constructor(port: number) {
    this.port = port;
    this.server = http.createServer();
  }

  get redirectPort() {
    return this.port;
  }

  get redirectUri() {
    return `http://localhost:${this.redirectPort}/oauth/openid/callback`;
  }

  /**
   * Starts the server and returns a promise that resolves with the callback URL.
   */
  start() {
    // Start the server
    this.server.listen(this.redirectPort, () => {
      logger.debug(
        `Local authentication server listening on port ${this.redirectPort}`,
      );
    });
  }

  waitForCallbackUrl() {
    // Create a promise that will resolve with the callback URL
    const codePromise = new Promise<URL>((resolve, reject) => {
      // Set a timeout
      const timeoutId = setTimeout(() => {
        reject(new Error("Authentication timed out"));
        this.server?.close();
      }, 120000); // 2 minute timeout

      this.server?.on("request", (req, res) => {
        try {
          if (!req.url) {
            throw new Error("Request URL is undefined");
          }

          // Parse the callback URL
          const callbackUrl = new URL(req.url, `http://${req.headers.host}`);

          // Parse the URL params and check for errors.
          if (callbackUrl.searchParams.has("error")) {
            const error = callbackUrl.searchParams.get("error");
            const errorDescription =
              callbackUrl.searchParams.get("error_description") ||
              "Unknown error";

            // Send an error response
            res.writeHead(400, { "Content-Type": "text/html" });
            res.end(`
                                      <html>
                                          <body>
                                              <h1>Authentication Failed</h1>
                                              <p>Error: ${error}</p>
                                              <p>${errorDescription}</p>
                                              <p>Please close this window and try again.</p>
                                          </body>
                                      </html>
                                  `);

            reject(
              new Error(`Authentication error: ${error} - ${errorDescription}`),
            );
          } else if (callbackUrl.searchParams.has("code")) {
            // Auth was successful, resolve the promise - ?code=...
            clearTimeout(timeoutId);
            resolve(callbackUrl);

            // Send a success response
            res.writeHead(200, { "Content-Type": "text/html" });
            res.end(`
                                      <html>
                                          <body>
                                              <h1>Authentication Successful</h1>
                                              <p>You can close this window and return to the CLI.</p>
                                              <script>setTimeout(function() { window.close(); }, 2000);</script>
                                          </body>
                                      </html>
                                  `);
          } else {
            // No code parameter found
            res.writeHead(400, { "Content-Type": "text/html" });
            res.end(`
                                      <html>
                                          <body>
                                              <h1>Authentication Failed</h1>
                                              <p>No authorization code was received.</p>
                                              <p>Please close this window and try again.</p>
                                          </body>
                                      </html>
                                  `);

            reject(new Error("No authorization code received"));
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : String(err);

          // Send an error response
          res.writeHead(500, { "Content-Type": "text/html" });
          res.end(`
                                  <html>
                                      <body>
                                          <h1>Authentication Failed</h1>
                                          <p>Error: ${errorMessage}</p>
                                          <p>Please close this window and try again.</p>
                                      </body>
                                  </html>
                              `);

          reject(new Error(`Error processing callback: ${errorMessage}`));
        }
      });
    });

    return codePromise;
  }

  stop() {
    this.server.close();
  }
}

export const loginFlow = async (
  oidcConfig: OidcConfig,
  output: Console = console,
  redirectPort: number = 3080,
): Promise<TokensResponse> => {
  const server = new OAuthCallbackServer(redirectPort);
  await server.start();

  try {
    // Discover OpenID configuration
    logger.debug("Discovering OAuth provider...");
    const oidcDiscoveredConfig = await discovery(
      new URL(oidcConfig.discoveryUrl ?? ""),
      oidcConfig.clientId ?? "",
    );

    // Generate PKCE parameters
    const code_verifier = randomPKCECodeVerifier();
    const code_challenge = await calculatePKCECodeChallenge(code_verifier);

    // Always generate a state parameter for CSRF protection
    const state = randomState();

    // Build the redirect URL with appropriate parameters
    const parameters: Record<string, string> = {
      redirect_uri: server.redirectUri,
      scope: "openid profile email",
      code_challenge,
      code_challenge_method: "S256",
      state, // Always include state parameter
    };

    // Build the authorization URL for login.
    const redirectTo = buildAuthorizationUrl(oidcDiscoveredConfig, parameters);

    // Open the browser for the user to authenticate

    try {
      await open(redirectTo.href);
      logger.debug("Browser opened automatically.");
    } catch (error) {
      logger.error(`Unable to open browser automatically: ${error}`);

      output.info("\n"); // Add a new line so it comes after the spinner.
      output.error("⚠️  Unable to open browser automatically.");
      output.info("\n");
      output.info("Please open this URL in your browser:");
      output.info("\n");
      output.info(redirectTo.href);
      output.info("\n");
    }

    const callbackUrl = await server.waitForCallbackUrl();

    // We don't need the server anymore.
    await server.stop();

    // Exchange the authorization code for tokens
    logger.debug("Exchanging code for tokens...");
    const tokens = await authorizationCodeGrant(
      oidcDiscoveredConfig,
      callbackUrl,
      {
        pkceCodeVerifier: code_verifier,
        expectedState: state, // Always verify the state parameter
      },
    );

    if (!tokens.access_token) {
      logger.error("Authentication failed. No access token received.");
      throw new Error("Authentication failed. No access token received.");
    }

    logger.debug("Tokens generated successfully.");

    return tokens;
  } catch (error) {
    logger.error("Failed signup flow: ", error);
    throw error;
  } finally {
    server.stop();
  }
};

export const loginFlowWithTimeout = async (
  oidcConfig: OidcConfig,
  output: Console = console,
  redirectPort: number = 3080,
  timeout: number = 5 * 60 * 1000, // Default to 5 minutes
): Promise<TokensResponse> => {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Authentication process timed out after ${timeout}ms`));
    }, timeout);
  });

  return Promise.race([
    loginFlow(oidcConfig, output, redirectPort),
    timeoutPromise,
  ]);
};
